import React, { forwardRef, useCallback, useEffect } from "react";

import { classNames, returnStringIfTrue } from "@app/helpers/componentHelpers";
import { useDeviceType } from "@app/hooks/useDeviceType";

import variables from "../exportables.module.scss";
import "./SidePanel.scss";

interface SidePanelProps {
  open?: boolean;
  hasSlider?: boolean;
  content?: React.ReactNode;
  subNavActive?: boolean;
  isDragging?: boolean;
  onMouseDown?: (event: React.MouseEvent) => void;
  defaultWidth?: string;
  previousWidth?: string;
}

const SidePanel = forwardRef<HTMLDivElement, SidePanelProps>(
  (
    {
      open,
      hasSlider,
      content,
      subNavActive,
      isDragging,
      onMouseDown,
      defaultWidth = "400px",
      previousWidth = defaultWidth
    },
    ref
  ) => {
    const { isMobile } = useDeviceType();

    const updateWidth = useCallback(() => {
      if (!ref) return;
      const refElement = (ref as React.MutableRefObject<HTMLDivElement>)
        .current;
      if (!refElement) return;

      if (!open) {
        refElement.style.width = "0px";
      } else {
        refElement.style.width = isMobile ? "100%" : previousWidth;
      }
    }, [previousWidth, open, ref, isMobile]);

    useEffect(() => {
      updateWidth();
    }, [updateWidth]);

    return (
      <div
        ref={ref}
        className={classNames([
          "side-panel",
          returnStringIfTrue(open, "side-panel--expanded")
        ])}
        style={{
          paddingTop: subNavActive ? variables.navigationBarHeight : 0,
          flexShrink: 0,
          flexGrow: isMobile ? 0 : 1,
          marginLeft: isMobile ? 0 : "auto"
        }}
      >
        {open && !isMobile && hasSlider && (
          <div
            className={classNames([
              "side-panel__slider",
              returnStringIfTrue(isDragging, "side-panel__slider--dragging")
            ])}
            onMouseDown={onMouseDown}
          />
        )}
        {open && content}
      </div>
    );
  }
);

SidePanel.displayName = "SidePanel";

export default SidePanel;
